import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { useUpdateMilestone, type ProjectMilestone } from "@/hooks/useProjects";
import { inlineDayLabel } from "@/components/record/recordFormat";
import { dueDay, dueTone } from "@shared/clientProgress";
import { cn } from "@/lib/utils";

/**
 * One milestone with its progress slider, in steps of 25. Moved from
 * ClientDetail.tsx. The due day is the stored date part, judged against today
 * in Central.
 *
 * The slider is controlled, and Radix commits a controlled slider only when its
 * value actually moved, so it moves through `draft`. The old row had no
 * onValueChange, so a drag or click never saved; only the arrow keys did.
 */
export function MilestoneRow({ milestone, projectId, companyId, today }: {
  milestone: ProjectMilestone; projectId: string; companyId: string; today: string;
}) {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateMilestone(projectId);
  const [draft, setDraft] = useState<number | null>(null);
  const progress = milestone.progress || 0;
  const shown = draft ?? progress;
  const day = dueDay(milestone.dueDate);
  const tone = progress < 100 ? dueTone(day, today) : null;

  const save = (value: number[]) => {
    updateMutation.mutate(
      { milestoneId: milestone.id, progress: value[0] },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ["client-project", companyId] });
          setDraft(null);
        },
        onError: (e: Error) => {
          setDraft(null);
          toast.error(e.message || "Couldn't save the progress");
        },
      },
    );
  };

  return (
    <div className="group flex items-center gap-3 py-1.5">
      <div className={cn(
        "h-2 w-2 shrink-0 rounded-full",
        progress >= 100 ? "bg-green-500" :
        milestone.priority === "High" ? "bg-red-500" :
        milestone.priority === "Medium" ? "bg-amber-500" : "bg-blue-500",
      )} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn("text-[13px]", progress >= 100 && "text-muted-foreground line-through")}>{milestone.name}</span>
          {progress >= 100 && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />}
        </div>
        {day && (
          <span className={cn(
            "text-[11px] tabular-nums",
            tone === "bad" ? "font-semibold text-destructive" : tone === "warn" ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground",
          )}>
            {tone === "bad" ? "Was due " : "Due "}{inlineDayLabel(day, today)}
          </span>
        )}
      </div>
      <div className="w-24 shrink-0">
        <Slider
          value={[shown]}
          max={100}
          step={25}
          onValueChange={(v) => setDraft(v[0])}
          onValueCommit={save}
          className="cursor-pointer"
          aria-label={`Progress on ${milestone.name}`}
        />
      </div>
      <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">{shown}%</span>
    </div>
  );
}
